"use client";

import { c as _c } from "react/compiler-runtime";
import { useClient } from "@opensea/graphql";
import { getRewrittenHomePage, usePathname } from "@opensea/navigation";
import { useIsLessThanMd } from "@opensea/ui-kit";
import { AccountCircle, Anchor, AutoAwesomeMosaic, CalendarToday, CalendarTodayFilled, Draw, Explore, ExploreFilled, Lists, SwapCalls, Toll } from "@opensea/ui-kit/icons";
import { normalizeAddress } from "@opensea/utils";
import { getChainArchFromAddress } from "@opensea/wallet";
import { useTranslations } from "next-intl";
import { prefetchProfileItemsPage } from "@/app/[locale]/[currency]/[persona]/(full-immersive)/profile/[heroHeaderView]/[identifier]/items/[userPreferences]/prefetch";
import { prefetchProfilePortfolioPage } from "@/app/[locale]/[currency]/[persona]/(full-immersive)/profile/[heroHeaderView]/[identifier]/portfolio/[userPreferences]/prefetch";
import { prefetchProfileTokensPage } from "@/app/[locale]/[currency]/[persona]/(full-immersive)/profile/[heroHeaderView]/[identifier]/tokens/[userPreferences]/prefetch";
import { prefetchGlobalActivityPage } from "@/hooks/api/activity/use-global-activity";
import { QUERY_PARAM_KEYS } from "@/hooks/query-param/keys";
import { useAddress } from "@/hooks/wallet";
import { trackDiscoverPageNavigation, trackDropsPageNavigation, trackProfileActivityPageNavigation, trackProfileCreatedPageNavigation, trackProfileFavoritesPageNavigation, trackProfileGalleriesPageNavigation, trackProfileListingsPageNavigation, trackProfileOffersPageNavigation, trackProfilePageNavigation, trackProfilePortfolioPageNavigation, trackProfileTokensPageNavigation, trackProfileWatchlistPageNavigation, trackStatsPageNavigation, trackSwapPageNavigation, trackTokenStatsPageNavigation } from "@/lib/performance";
import { getProfileUrlByIdentifier } from "../profile/utils";
import { prefetchCurrencyStats } from "../stats/TopCurrencyStatsTable/prefetch";
import { prefetchStats } from "../stats/TopStatsTable/prefetch";

// Regex constants for pathname normalization
const OPEN_STATE_REGEX = /\/(?<openState>open|closed)\//g;
const TABLE_VIEW_REGEX = /\/(?<tableView>tablecompact|table)(?<pathEnd>\/|$)/g;
const TRAILING_SLASH_REGEX = /(.+)\/$/;
export const useNavItems = () => {
  const $ = _c(146);
  const client = useClient();
  const address = useAddress();
  const isLessThanMd = useIsLessThanMd();
  const t = useTranslations("useNavItems");
  let t0;
  if ($[0] !== t) {
    t0 = t("discover");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      id: "discover",
      title: t0,
      href: "/",
      icon: Explore,
      activeIcon: ExploreFilled,
      onNavigationCompleted: trackDiscoverPageNavigation
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("collections");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== client) {
    t3 = () => {
      prefetchStats(client);
    };
    $[6] = client;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = {
      id: "nft-stats",
      title: t2,
      href: "/stats",
      icon: AutoAwesomeMosaic,
      activeIcon: AutoAwesomeMosaic,
      onNavigationCompleted: trackStatsPageNavigation,
      prefetch: t3,
      "data-testid": "nft-stats"
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t) {
    t5 = t("tokens");
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== client) {
    t6 = () => {
      prefetchCurrencyStats(client);
    };
    $[13] = client;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5 || $[16] !== t6) {
    t7 = {
      id: "token-stats",
      title: t5,
      href: "/stats/tokens",
      icon: Toll,
      activeIcon: Toll,
      onNavigationCompleted: trackTokenStatsPageNavigation,
      prefetch: t6,
      "data-testid": "token-stats"
    };
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t) {
    t8 = t("swap");
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = {
      id: "swap",
      title: t8,
      href: "/swap",
      icon: SwapCalls,
      activeIcon: SwapCalls,
      onNavigationCompleted: trackSwapPageNavigation
    };
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t) {
    t10 = t("drops");
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10) {
    t11 = {
      id: "drops",
      title: t10,
      href: "/drops",
      icon: CalendarToday,
      activeIcon: CalendarTodayFilled,
      onNavigationCompleted: trackDropsPageNavigation
    };
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t) {
    t12 = t("profile");
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== address) {
    t13 = getProfileUrlByIdentifier(address);
    $[28] = address;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t) {
    t14 = t("portfolio");
    $[30] = t;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const t15 = `${getProfileUrlByIdentifier(address)}/portfolio`;
  let t16;
  if ($[32] !== address || $[33] !== client) {
    t16 = address ? () => {
      prefetchProfilePortfolioPage(address, client);
    } : undefined;
    $[32] = address;
    $[33] = client;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t14 || $[36] !== t15 || $[37] !== t16) {
    t17 = {
      id: "profile-portfolio",
      title: t14,
      href: t15,
      onNavigationCompleted: trackProfilePortfolioPageNavigation,
      prefetch: t16
    };
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t) {
    t18 = t("nfts");
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  const t19 = `${getProfileUrlByIdentifier(address)}/items`;
  let t20;
  if ($[41] !== address || $[42] !== client) {
    t20 = address ? () => {
      if (getChainArchFromAddress(address) === "SVM") {
        prefetchProfileTokensPage(address, client);
      } else {
        prefetchProfileItemsPage(address, client);
      }
    } : undefined;
    $[41] = address;
    $[42] = client;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t18 || $[45] !== t19 || $[46] !== t20) {
    t21 = {
      id: "profile-nfts",
      title: t18,
      href: t19,
      onNavigationCompleted: trackProfilePageNavigation,
      prefetch: t20
    };
    $[44] = t18;
    $[45] = t19;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t) {
    t22 = t("tokens");
    $[48] = t;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  const t23 = `${getProfileUrlByIdentifier(address)}/tokens`;
  let t24;
  if ($[50] !== address || $[51] !== client) {
    t24 = address ? () => {
      prefetchProfileTokensPage(address, client);
    } : undefined;
    $[50] = address;
    $[51] = client;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t22 || $[54] !== t23 || $[55] !== t24) {
    t25 = {
      id: "profile-tokens",
      title: t22,
      href: t23,
      onNavigationCompleted: trackProfileTokensPageNavigation,
      prefetch: t24
    };
    $[53] = t22;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t) {
    t26 = t("listings");
    $[57] = t;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  const t27 = `${getProfileUrlByIdentifier(address)}/listings`;
  let t28;
  if ($[59] !== t26 || $[60] !== t27) {
    t28 = {
      id: "profile-listings",
      title: t26,
      href: t27,
      onNavigationCompleted: trackProfileListingsPageNavigation
    };
    $[59] = t26;
    $[60] = t27;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t) {
    t29 = t("offers");
    $[62] = t;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  const t30 = `${getProfileUrlByIdentifier(address)}/offers`;
  let t31;
  if ($[64] !== t29 || $[65] !== t30) {
    t31 = {
      id: "profile-offers",
      title: t29,
      href: t30,
      onNavigationCompleted: trackProfileOffersPageNavigation
    };
    $[64] = t29;
    $[65] = t30;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== t) {
    t32 = t("galleries");
    $[67] = t;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  const t33 = `${getProfileUrlByIdentifier(address)}/galleries`;
  let t34;
  if ($[69] !== t32 || $[70] !== t33) {
    t34 = {
      id: "profile-galleries",
      title: t32,
      href: t33,
      onNavigationCompleted: trackProfileGalleriesPageNavigation
    };
    $[69] = t32;
    $[70] = t33;
    $[71] = t34;
  } else {
    t34 = $[71];
  }
  let t35;
  if ($[72] !== t) {
    t35 = t("created");
    $[72] = t;
    $[73] = t35;
  } else {
    t35 = $[73];
  }
  const t36 = `${getProfileUrlByIdentifier(address)}/created`;
  let t37;
  if ($[74] !== t35 || $[75] !== t36) {
    t37 = {
      id: "profile-created",
      title: t35,
      href: t36,
      onNavigationCompleted: trackProfileCreatedPageNavigation
    };
    $[74] = t35;
    $[75] = t36;
    $[76] = t37;
  } else {
    t37 = $[76];
  }
  let t38;
  if ($[77] !== t) {
    t38 = t("watchlist");
    $[77] = t;
    $[78] = t38;
  } else {
    t38 = $[78];
  }
  const t39 = `${getProfileUrlByIdentifier(address)}/watchlist`;
  let t40;
  if ($[79] !== t38 || $[80] !== t39) {
    t40 = {
      id: "profile-watchlist",
      title: t38,
      href: t39,
      onNavigationCompleted: trackProfileWatchlistPageNavigation
    };
    $[79] = t38;
    $[80] = t39;
    $[81] = t40;
  } else {
    t40 = $[81];
  }
  let t41;
  if ($[82] !== t) {
    t41 = t("favorites");
    $[82] = t;
    $[83] = t41;
  } else {
    t41 = $[83];
  }
  const t42 = `${getProfileUrlByIdentifier(address)}/favorites`;
  let t43;
  if ($[84] !== t41 || $[85] !== t42) {
    t43 = {
      id: "profile-favorites",
      title: t41,
      href: t42,
      onNavigationCompleted: trackProfileFavoritesPageNavigation
    };
    $[84] = t41;
    $[85] = t42;
    $[86] = t43;
  } else {
    t43 = $[86];
  }
  let t44;
  if ($[87] !== t) {
    t44 = t("activity");
    $[87] = t;
    $[88] = t44;
  } else {
    t44 = $[88];
  }
  const t45 = `${getProfileUrlByIdentifier(address)}/activity`;
  let t46;
  if ($[89] !== t44 || $[90] !== t45) {
    t46 = {
      id: "profile-activity",
      title: t44,
      href: t45,
      onNavigationCompleted: trackProfileActivityPageNavigation
    };
    $[89] = t44;
    $[90] = t45;
    $[91] = t46;
  } else {
    t46 = $[91];
  }
  let t47;
  if ($[92] !== t17 || $[93] !== t21 || $[94] !== t25 || $[95] !== t28 || $[96] !== t31 || $[97] !== t34 || $[98] !== t37 || $[99] !== t40 || $[100] !== t43 || $[101] !== t46) {
    t47 = [t17, t21, t25, t28, t31, t34, t37, t40, t43, t46];
    $[92] = t17;
    $[93] = t21;
    $[94] = t25;
    $[95] = t28;
    $[96] = t31;
    $[97] = t34;
    $[98] = t37;
    $[99] = t40;
    $[100] = t43;
    $[101] = t46;
    $[102] = t47;
  } else {
    t47 = $[102];
  }
  let t48;
  if ($[103] !== address || $[104] !== client) {
    t48 = address ? () => {
      prefetchProfilePortfolioPage(address, client);
    } : undefined;
    $[103] = address;
    $[104] = client;
    $[105] = t48;
  } else {
    t48 = $[105];
  }
  let t49;
  if ($[106] !== t12 || $[107] !== t13 || $[108] !== t47 || $[109] !== t48) {
    t49 = {
      id: "profile",
      title: t12,
      href: t13,
      icon: AccountCircle,
      activeIcon: AccountCircle,
      subItems: t47,
      onNavigationCompleted: trackProfilePageNavigation,
      prefetch: t48
    };
    $[106] = t12;
    $[107] = t13;
    $[108] = t47;
    $[109] = t48;
    $[110] = t49;
  } else {
    t49 = $[110];
  }
  let t50;
  if ($[111] !== t) {
    t50 = t("activity");
    $[111] = t;
    $[112] = t50;
  } else {
    t50 = $[112];
  }
  let t51;
  if ($[113] !== client) {
    t51 = () => {
      prefetchGlobalActivityPage(client);
    };
    $[113] = client;
    $[114] = t51;
  } else {
    t51 = $[114];
  }
  let t52;
  if ($[115] !== t50 || $[116] !== t51) {
    t52 = {
      id: "activity",
      title: t50,
      href: "/activity",
      icon: Lists,
      activeIcon: Lists,
      prefetch: t51
    };
    $[115] = t50;
    $[116] = t51;
    $[117] = t52;
  } else {
    t52 = $[117];
  }
  let t53;
  if ($[118] !== t) {
    t53 = t("studio");
    $[118] = t;
    $[119] = t53;
  } else {
    t53 = $[119];
  }
  let t54;
  if ($[120] !== t53) {
    t54 = {
      id: "studio",
      title: t53,
      href: "/studio",
      icon: Draw,
      activeIcon: Draw
    };
    $[120] = t53;
    $[121] = t54;
  } else {
    t54 = $[121];
  }
  let t55;
  if ($[122] !== t) {
    t55 = t("rewards");
    $[122] = t;
    $[123] = t55;
  } else {
    t55 = $[123];
  }
  let t56;
  if ($[124] !== t55) {
    t56 = {
      id: "rewards",
      title: t55,
      href: "/rewards",
      icon: Anchor,
      activeIcon: Anchor
    };
    $[124] = t55;
    $[125] = t56;
  } else {
    t56 = $[125];
  }
  let t57;
  if ($[126] !== t1 || $[127] !== t11 || $[128] !== t4 || $[129] !== t49 || $[130] !== t52 || $[131] !== t54 || $[132] !== t56 || $[133] !== t7 || $[134] !== t9) {
    t57 = {
      discover: t1,
      nftStats: t4,
      tokenStats: t7,
      swap: t9,
      drops: t11,
      profile: t49,
      activity: t52,
      studio: t54,
      rewards: t56
    };
    $[126] = t1;
    $[127] = t11;
    $[128] = t4;
    $[129] = t49;
    $[130] = t52;
    $[131] = t54;
    $[132] = t56;
    $[133] = t7;
    $[134] = t9;
    $[135] = t57;
  } else {
    t57 = $[135];
  }
  const navItems = t57;
  const t58 = !isLessThanMd && navItems.studio;
  let t59;
  if ($[136] !== navItems.activity || $[137] !== navItems.discover || $[138] !== navItems.drops || $[139] !== navItems.nftStats || $[140] !== navItems.profile || $[141] !== navItems.rewards || $[142] !== navItems.swap || $[143] !== navItems.tokenStats || $[144] !== t58) {
    t59 = [navItems.discover, navItems.nftStats, navItems.tokenStats, navItems.swap, navItems.drops, navItems.activity, navItems.rewards, t58, navItems.profile].filter(Boolean);
    $[136] = navItems.activity;
    $[137] = navItems.discover;
    $[138] = navItems.drops;
    $[139] = navItems.nftStats;
    $[140] = navItems.profile;
    $[141] = navItems.rewards;
    $[142] = navItems.swap;
    $[143] = navItems.tokenStats;
    $[144] = t58;
    $[145] = t59;
  } else {
    t59 = $[145];
  }
  const items = t59;
  return items;
};
const isItemActive = (pathname, item, connectedAddress, {
  exactPath = false
} = {}) => {
  const normalizedItemHref = item.href.toLowerCase();
  // Vercel bug causes pathname here to be the rewritten pathname from middleware only on Vercel
  const normalizedPathname = pathname.toLowerCase().replace(OPEN_STATE_REGEX, "/").replace(TABLE_VIEW_REGEX, "/").replace(TRAILING_SLASH_REGEX, "$1"); // Only remove trailing slash if it's not the only character

  if (normalizedItemHref === "/") {
    if (exactPath ? normalizedPathname === getRewrittenHomePage() : normalizedPathname.startsWith(getRewrittenHomePage())) {
      return true;
    }
    // Remove query params
    return normalizedItemHref.split("?")[0] === normalizedPathname;
  }
  if (normalizedItemHref === "/stats") {
    if (normalizedPathname.startsWith("/stats/tokens")) {
      return false;
    }
  }

  // Exact match only for profile page
  if (connectedAddress) {
    const normalizedAddress = normalizeAddress(connectedAddress);
    if (normalizedItemHref === `/${normalizedAddress}` && normalizedPathname !== `/${normalizedAddress}`) {
      return false;
    }
  }
  if (normalizedItemHref === "/profile" && normalizedPathname !== "/profile") {
    return false;
  }
  return exactPath ? normalizedItemHref === normalizedPathname :
  // Remove query params
  normalizedPathname.startsWith(normalizedItemHref.split("?")[0]);
};
export const useIsItemActive = options => {
  const $ = _c(4);
  const pathname = usePathname();
  const connectedAddress = useAddress();
  let t0;
  if ($[0] !== connectedAddress || $[1] !== options || $[2] !== pathname) {
    t0 = item => isItemActive(pathname, item, connectedAddress, options);
    $[0] = connectedAddress;
    $[1] = options;
    $[2] = pathname;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
export const queryParamsWithoutSort = queryParams => {
  const copiedQueryParams = new URLSearchParams(queryParams);
  copiedQueryParams.delete(QUERY_PARAM_KEYS.sortBy);
  copiedQueryParams.delete(QUERY_PARAM_KEYS.sortDirection);
  return copiedQueryParams;
};